import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import Index from "@/pages/Index";
import Medications from "@/pages/Medications";
import Appointments from "@/pages/Appointments";
import Journal from "@/pages/Journal";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Services from "@/pages/Services";
import Messages from "@/pages/Messages";
import ProfessionalDashboard from "@/pages/ProfessionalDashboard";
import ProfessionalServices from "@/pages/ProfessionalServices";
import ProfessionalAppointments from "@/pages/ProfessionalAppointments";
import ProfessionalPatients from "@/pages/ProfessionalPatients";
import VirtualFirstAid from "@/pages/VirtualFirstAid";
import { ProductDetails } from "@/components/services/products/ProductDetails";
import { ServiceDetails } from "@/components/services/ServiceDetails";
import PeriodTracking from "@/pages/PeriodTracking";
import GestationTracking from "@/pages/GestationTracking";
import { NotFound } from "@/pages/NotFound";
import Auth from "@/pages/Auth";
import AiAssistance from "@/pages/AiAssistance";
import { useAuth } from "@/hooks/use-auth";
import ProfessionalManagement from "@/pages/ProfessionalManagement";
import Profile from "@/pages/Profile";
import EditProfile from "@/pages/EditProfile";
import MobileMarketplace from "@/pages/MobileMarketplace";
import MobileMedications from "@/pages/mobile/MobileMedications";
import MobileAppointments from "@/pages/mobile/MobileAppointments";
import MobileJournal from "@/pages/mobile/MobileJournal";
import MobilePeriodTracking from "@/pages/mobile/MobilePeriodTracking";
import MobileGestationTracking from "@/pages/mobile/MobileGestationTracking";
import MobileAiAssistance from "@/pages/mobile/MobileAiAssistance";
import MobileMessages from "@/pages/mobile/MobileMessages";
import Mobile404 from "@/pages/mobile/Mobile404";
import MobileNotifications from "@/pages/mobile/MobileNotifications";
import MobileProfessionalDashboard from "@/pages/mobile/MobileProfessionalDashboard";
import MobileProfessionalPatients from "@/pages/mobile/MobileProfessionalPatients";
import MobileProfessionalAppointments from "@/pages/mobile/MobileProfessionalAppointments";
import MobileProfessionalServices from "@/pages/mobile/MobileProfessionalServices";
import MobileProfessionalManagement from "@/pages/mobile/MobileProfessionalManagement";
import MobileServiceDetails from "@/pages/mobile/MobileServiceDetails";
import MobileProductDetails from "@/pages/mobile/MobileProductDetails";
import Notifications from "@/pages/Notifications";
import { useIsMobile } from "@/hooks/use-mobile";
import { Suspense, useEffect } from "react";
import MobileLayout from "@/components/layout/MobileLayout";
import Donation from "@/pages/Donation";
import { UserManagement } from "@/components/admin/user-management";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";


export const AppRoutes = () => {
  const location = useLocation();
  const { session, isLoading, isAuthenticated } = useAuth();
  const isMobile = useIsMobile();
  
  useEffect(() => {
    if (location.pathname === '/404') {
      window.location.href = '/';
    }
  }, [location.pathname]);
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isMobile) {
    
    return (
      <Routes>
        <Route path="/auth" element={<Auth />} />

        <Route element={          <Suspense fallback={<div className="min-h-screen flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>}>
            <MobileLayout />
          </Suspense>
        }>
          <Route path="/" element={<Index />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/services" element={<Services />} />
          <Route path="/marketplace-mobile" element={<MobileMarketplace />} />
          
          <Route path="/service-detail-mobile/:id" element={<MobileServiceDetails />} />
          <Route path="/product-detail-mobile/:id" element={<MobileProductDetails />} />
          
          <Route path="/donation" element={<Donation />} />
          
          <Route path="/medicaments-mobile" element={
            isAuthenticated ? <MobileMedications /> : <Navigate to="/auth" replace />
          } />
          <Route path="/rendez-vous-mobile" element={
            isAuthenticated ? <MobileAppointments /> : <Navigate to="/auth" replace />
          } />
          <Route path="/journal-mobile" element={
            isAuthenticated ? <MobileJournal /> : <Navigate to="/auth" replace />
          } />
          <Route path="/period-tracking-mobile" element={
            isAuthenticated ? <MobilePeriodTracking /> : <Navigate to="/auth" replace />
          } />
          <Route path="/gestation-tracking-mobile" element={
            isAuthenticated ? <MobileGestationTracking /> : <Navigate to="/auth" replace />
          } />
          <Route path="/ai-assistance-mobile" element={
            isAuthenticated ? <MobileAiAssistance /> : <Navigate to="/auth" replace />
          } />
          <Route path="/messages-mobile" element={
            isAuthenticated ? <MobileMessages /> : <Navigate to="/auth" replace />
          } />
          <Route path="/notifications" element={
            isAuthenticated ? <MobileNotifications /> : <Navigate to="/auth" replace />
          } />
          <Route path="/professional-mobile" element={
            isAuthenticated ? <MobileProfessionalDashboard /> : <Navigate to="/auth" replace />
          } />
          <Route path="/professional-mobile/patients" element={
            isAuthenticated ? <MobileProfessionalPatients /> : <Navigate to="/auth" replace />
          } />
          <Route path="/professional-mobile/appointments" element={
            isAuthenticated ? <MobileProfessionalAppointments /> : <Navigate to="/auth" replace />
          } />
          <Route path="/professional-mobile/services" element={
            isAuthenticated ? <MobileProfessionalServices /> : <Navigate to="/auth" replace />
          } />
          <Route path="/professional-mobile/management" element={
            isAuthenticated ? <MobileProfessionalManagement /> : <Navigate to="/auth" replace />
          } />
          
          <Route path="/professional/management" element={
            isAuthenticated ? <ProfessionalManagement /> : <Navigate to="/auth" replace />
          } />
          <Route path="/professional/management/:section" element={
            isAuthenticated ? <ProfessionalManagement /> : <Navigate to="/auth" replace />
          } />
          
          <Route path="/profile" element={
            isAuthenticated ? <Profile /> : <Navigate to="/auth" replace />
          } />
          <Route path="/profile/edit" element={
            isAuthenticated ? <EditProfile /> : <Navigate to="/auth" replace />
          } />
          
          <Route path="/admin/users" 
            element={
              isAuthenticated ? <UserManagement /> : <Navigate to="/auth" replace />
            }
          />
          <Route path="/404" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/auth" element={<Auth />} />

      <Route element={<Layout />}>
        <Route path="/" element={<Index />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/services" element={<Services />} />
        
        <Route path="/services/:id" element={<ServiceDetails />} />
        <Route path="/services/products/:id" element={<ProductDetails />} />
        
        <Route path="/donation" element={<Donation />} />
      
        <Route path="/profile" element={
          isAuthenticated ? <Profile /> : <Navigate to="/auth" replace />
        } />
        <Route path="/profile/edit" element={
          isAuthenticated ? <EditProfile /> : <Navigate to="/auth" replace />
        } />
        <Route path="/medicaments" element={
          isAuthenticated ? <Medications /> : <Navigate to="/auth" replace />
        } />
        <Route path="/rendez-vous" element={
          isAuthenticated ? <Appointments /> : <Navigate to="/auth" replace />
        } />
        <Route path="/journal" element={
          isAuthenticated ? <Journal /> : <Navigate to="/auth" replace />
        } />
        <Route path="/messages" element={
          isAuthenticated ? <Messages /> : <Navigate to="/auth" replace />
        } />
        <Route path="/notifications" element={
          isAuthenticated ? <Notifications /> : <Navigate to="/auth" replace />
        } />
        <Route path="/period-tracking" element={
          isAuthenticated ? <PeriodTracking /> : <Navigate to="/auth" replace />
        } />
        <Route path="/gestation-tracking" element={
          isAuthenticated ? <GestationTracking /> : <Navigate to="/auth" replace />
        } />
        <Route path="/ai-assistance" element={
          isAuthenticated ? <AiAssistance /> : <Navigate to="/auth" replace />
        } />
        <Route path="/virtual-first-aid" element={
          isAuthenticated ? <VirtualFirstAid /> : <Navigate to="/auth" replace />
        } />

        <Route path="/professional" element={
          isAuthenticated ? <ProfessionalDashboard /> : <Navigate to="/auth" replace />
        } />
        <Route path="/professional/services" element={
          isAuthenticated ? <ProfessionalServices /> : <Navigate to="/auth" replace />
        } />
        <Route path="/professional/appointments" element={
          isAuthenticated ? <ProfessionalAppointments /> : <Navigate to="/auth" replace />
        } />
        <Route path="/professional/patients" element={
          isAuthenticated ? <ProfessionalPatients /> : <Navigate to="/auth" replace />
        } />
        <Route path="/professional/management" element={
          isAuthenticated ? <ProfessionalManagement /> : <Navigate to="/auth" replace />
        } />
        
        <Route path="/admin/users" 
          element={
            isAuthenticated ? (
              <UserManagement />
            ) : <Navigate to="/auth" replace />
          }
        />

        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
};

export default AppRoutes;
